// Helpers for the data-driven attribute popover engine. Ports the
// composite-key + cascade-clear logic from the prototype at
// `src/Mapping UI/index.html:510-640`.
//
// Convention: a field def stores numeric values inside `options[].value`,
// but cross-field lookup tables (`displayLabelByValue`, `optionsByValue`)
// are keyed by LABEL strings. The engine resolves parent slot values
// (numeric) → labels via the parent field's option array before lookup.

const asArray = (x) => (Array.isArray(x) ? x : [x]);

// Look up the label that maps to a numeric value inside a field's option list.
const valueToLabel = (field, numericValue) => {
  if (numericValue === undefined || numericValue === null) return undefined;
  const match = field.options.find((o) => o.value === numericValue);
  return match ? match.label : undefined;
};

const findFieldBySlot = (fields, slot) =>
  fields.find((f) => f.slot === slot) || null;

// Convert the current `values` object (slot → numeric value) into a
// parallel object of (slot → LABEL) using each field's option array.
const labelsFromValues = (fields, values) => {
  const out = {};
  for (const f of fields) {
    const v = values[f.slot];
    if (v !== undefined && v !== null) {
      const label = valueToLabel(f, v);
      if (label !== undefined) out[f.slot] = label;
    }
  }
  return out;
};

// Order of options follows the lookup-key array (so an `optionsByValue` of
// ["SHORT","MEDIUM","TALL"] renders in that order even when field.options
// declares them in a different order). Falls through to field.options when
// no dependency match.
const pickInLookupOrder = (field, allowedLabels) =>
  allowedLabels
    .map((lbl) => field.options.find((o) => o.label === lbl))
    .filter(Boolean);

export const getEffectiveOptions = (field, fields, values) => {
  if (!field.dependsOn || !field.optionsByValue) return field.options;

  const parentLabels = labelsFromValues(fields, values);
  const deps = asArray(field.dependsOn);

  // Composite key first.
  if (deps.length > 1 && deps.every((d) => parentLabels[d] !== undefined)) {
    const compositeKey = deps.map((d) => parentLabels[d]).join(" | ");
    if (field.optionsByValue[compositeKey] !== undefined) {
      return pickInLookupOrder(field, field.optionsByValue[compositeKey]);
    }
  }

  // Single-parent fallbacks, dependsOn order.
  for (const dep of deps) {
    const parentLabel = parentLabels[dep];
    if (parentLabel !== undefined && field.optionsByValue[parentLabel] !== undefined) {
      return pickInLookupOrder(field, field.optionsByValue[parentLabel]);
    }
  }

  if (field.fallback !== undefined) {
    return pickInLookupOrder(field, field.fallback);
  }

  return field.options;
};

export const getEffectiveDisplayLabel = (field, fields, values) => {
  if (field.displayLabelByValue && field.dependsOn) {
    const parentLabels = labelsFromValues(fields, values);
    const deps = asArray(field.dependsOn);

    if (deps.length > 1 && deps.every((d) => parentLabels[d] !== undefined)) {
      const compositeKey = deps.map((d) => parentLabels[d]).join(" | ");
      if (field.displayLabelByValue[compositeKey]) {
        return field.displayLabelByValue[compositeKey];
      }
    }
    for (const dep of deps) {
      const parentLabel = parentLabels[dep];
      if (parentLabel !== undefined && field.displayLabelByValue[parentLabel]) {
        return field.displayLabelByValue[parentLabel];
      }
    }
  }
  return field.displayLabel || field.label;
};

// Return a new `values` object with downstream child selections cleared
// whenever they're no longer valid against the new parent state.
export const cascadeClear = (fields, values, changedSlot) => {
  const next = { ...values };
  let progressed = true;
  // Run to a fixed point — a child clear may invalidate a grandchild.
  while (progressed) {
    progressed = false;
    for (const f of fields) {
      const deps = f.dependsOn ? asArray(f.dependsOn) : [];
      if (!deps.length) continue;
      if (next[f.slot] === undefined || next[f.slot] === null) continue;
      const effective = getEffectiveOptions(f, fields, next);
      const stillValid = effective.some((o) => o.value === next[f.slot]);
      if (!stillValid) {
        next[f.slot] = undefined;
        progressed = true;
      }
    }
    // After first pass, the changedSlot is the trigger; subsequent passes
    // catch transitively invalidated children regardless of which slot fired.
    if (changedSlot) changedSlot = null;
  }
  return next;
};

// Pass-through. Legacy `0` (NONE) is kept in state so:
//   * the popover renders no radio active (no option has value=0 in the picker)
//   * validation `=== undefined` accepts it (so the user can re-save without picking)
//   * dispatch (`!== undefined`) re-sends 0 to keep the backend value stable
//   * propertiesChange's `hasAttributeChanged(0, 0)` returns false — no spurious write
export const normalizeLoadedValue = (numericValue) =>
  numericValue === null ? undefined : numericValue;

export { findFieldBySlot };
